import { debounce } from 'lodash';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

export const useScrollBlock = (shouldBlock?: boolean) => {
  const isBlocking = useRef(false);
  const [clientWidth, setClientWidth] = useState(
    document.documentElement.clientWidth,
  );

  const blockScroll = useCallback((clientWidthAtCall: number) => {
    if (typeof document === 'undefined') return;

    const { body } = document;
    if (!body || !body.style || isBlocking.current) return;

    const scrollBarWidth = window.innerWidth - clientWidthAtCall;
    const bodyPaddingRight =
      parseInt(
        window.getComputedStyle(body).getPropertyValue('padding-right'),
        10,
      ) || 0;

    body.style.overflow = 'hidden';
    body.style.paddingRight = `${bodyPaddingRight + scrollBarWidth}px`;

    isBlocking.current = true;
  }, []);

  const allowScroll = useCallback(() => {
    if (typeof document === 'undefined') return;
    const { body } = document;

    if (!body || !body.style || !isBlocking.current) return;

    body.style.overflow = '';
    body.style.paddingRight = '';

    isBlocking.current = false;
  }, []);

  const setNewClientWidth = useCallback(() => {
    setClientWidth(document.documentElement.clientWidth);
  }, []);

  useEffect(() => {
    const handleResize = debounce(setNewClientWidth, 200);
    window.addEventListener('resize', handleResize);
    return () => {
      handleResize.cancel();
      window.removeEventListener('resize', handleResize);
    };
  }, [setNewClientWidth]);

  useEffect(() => {
    setNewClientWidth();
  }, [shouldBlock, setNewClientWidth]);

  useLayoutEffect(() => {
    if (shouldBlock) {
      blockScroll(clientWidth);
    } else {
      allowScroll();
    }
    return allowScroll;
  }, [shouldBlock, blockScroll, allowScroll, clientWidth]);
};
